import type { ApiClient, QueryParams, RequestOptions } from '@buildex/module-contract';
import { ApiError } from './errors';
import { coreEnv } from '../env';
import { getCurrentTenantSlug } from '../tenant/current-tenant';
import { getSessionToken } from '../session/token-ref';
import { isShellMockPath, mockRequest } from './mock-server';

function buildQuery(query?: QueryParams): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * Единственное место, где в запрос подставляется аутентификация (FRONTEND_GUIDE §3.2):
 * header-режим → X-Tenant-Id, oidc-режим → Authorization: Bearer.
 */
function authHeaders(): Record<string, string> {
  if (coreEnv.authMode === 'oidc') {
    const token = getSessionToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  const tenantSlug = getCurrentTenantSlug();
  return tenantSlug ? { 'X-Tenant-Id': tenantSlug } : {};
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  // Полные моки — либо эндпоинты shell'а, которых у App API пока нет (см. mock-server.ts)
  if (coreEnv.mockEnabled || isShellMockPath(path)) {
    return mockRequest<T>(method, path, body, options?.query);
  }

  const response = await fetch(`${coreEnv.apiBaseUrl}${path}${buildQuery(options?.query)}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
      ...options?.headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: options?.signal,
  });

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = undefined;
    }
    throw new ApiError(response.status, `${method} ${path} → ${response.status}`, errorBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Единственный api-клиент платформы: аутентификация добавляется автоматически */
export const apiClient: ApiClient = {
  get: (path, options) => request('GET', path, undefined, options),
  post: (path, body, options) => request('POST', path, body, options),
  put: (path, body, options) => request('PUT', path, body, options),
  patch: (path, body, options) => request('PATCH', path, body, options),
  delete: (path, options) => request('DELETE', path, undefined, options),
};
